import { observer } from "mobx-react";
import React, {
    CSSProperties,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    Alert,
    Box,
    Checkbox,
    ClickAwayListener,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    Paper,
    Popper,
    Typography,
} from "@mui/material";
import DownChevronSvg from "./BaseSelectComponents/DownChevronSvg";
import AcxButton from "../AcxButton";
import AcxMainTextField from "../AcxMainTextField";
import ClearIcon from "@mui/icons-material/Clear";
import theme from "Theme/AppTheme";
import CircularProgress from "@mui/material/CircularProgress";
import { FixedSizeList as List } from "react-window";
import _ from "lodash";
import type { DisabledAppearance } from "./BaseSelectComponents/AcxSelect";

interface Props<T> {
    id: string;
    onApply?: (selectedIds: string[]) => void;
    maxSelectionCount?: number;
    warningMessage?: string;
    defaultSelectedIds?: string[];
    options: T[];
    idField?: string;
    labelField?: string;
    isLoading?: boolean;
    itemName?: string;
    /**
     * Spells the pluralized `itemName` into the Select All button -- "Select All Agent Managers"
     * rather than the bare "Select All", matching how AcxVirtualizedSelectMultiGrouped always
     * labels it. Off by default so existing call sites keep the label they ship with today.
     */
    showItemNameInSelectAll?: boolean;
    enableCompactLayout?: boolean;
    controlsThreshold?: number;
    maxListHeight?: number;
    sortOnOpenOnly?: boolean;
    sortSelectedToTop?: boolean;
    renderOptionLabel?: (option: T) => React.ReactNode;
    /**
     * Lets the open menu grow wider than the select it drops from, so long option names have
     * room. The menu then lines up with the select's left edge instead of centering under it.
     */
    menuMinWidth?: number;
    /**
     * Wraps long option names onto a second line, breaking between words, instead of cutting
     * them off at one line. Rows grow a little taller to fit two lines; a name longer than two
     * lines still ends in an ellipsis, with the full name in its tooltip.
     */
    wrapOptionLabels?: boolean;
    disabled?: boolean;
    /**
     * Controls only the visual treatment of a disabled select. `readOnly`
     * keeps the value at full contrast with a transparent background and the
     * normal border, and hides the dropdown indicator; the select remains
     * semantically and functionally disabled.
     */
    disabledAppearance?: DisabledAppearance;
}

// Two lines at most, broken between words. A single word too long for a line is the only
// thing that breaks mid-word; the inherited `overflowWrap: "anywhere"` would split any word.
const wrappedOptionLabelSx = {
    minWidth: 0,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    lineHeight: 1.25,
    wordBreak: "normal",
    overflowWrap: "break-word",
} as const;

const areSelectedIdsEqual = (
    selectedIds: string[],
    defaultSelectedIds: string[] | undefined,
) => _.isEqual([...selectedIds].sort(), [...(defaultSelectedIds ?? [])].sort());

const AcxVirtualizedSelectMulti = observer(<T,>(props: Props<T>) => {
    const hasReadOnlyDisabledAppearance =
        props.disabled && props.disabledAppearance === "readOnly";
    const [isOpen, setIsOpen] = useState(false);

    const [selectedIds, setSelectedIds] = useState<string[]>(
        props.defaultSelectedIds ?? [],
    );
    const currentSelectedIds = props.defaultSelectedIds ?? selectedIds;

    const [options, setOptions] = useState<T[]>(props.options);

    const [searchTerm, setSearchTerm] = useState("");

    const ref = useRef<HTMLDivElement | null>(null);

    const searchInputRef = useRef<HTMLInputElement | null>(null);

    const clientWidth = ref.current?.clientWidth ?? 400;
    const menuWidth = Math.max(clientWidth, props.menuMinWidth ?? 0);
    const isMenuWiderThanSelect = menuWidth > clientWidth;

    const localIdField = props.idField ?? "id";
    const localLabelField = props.labelField ?? "label";
    const sortSelectedToTop = props.sortSelectedToTop ?? true;

    const compactLayoutEnabled = props.enableCompactLayout ?? false;
    const controlsThreshold = props.controlsThreshold ?? 10;
    const wrapOptionLabels = props.wrapOptionLabels ?? false;
    const listItemHeight = wrapOptionLabels ? 40 : 32;
    // Wrapped text must break inside the visible row, so leave room for the row padding, the
    // checkbox and a scrollbar rather than letting the last words run under the clipped edge.
    const wrappedLabelWidth = menuWidth - 72;
    const maxListHeight = props.maxListHeight ?? 300;

    const sortOptions = (optionsArray: T[], selectedIds: string[]) => {
        if (!sortSelectedToTop) {
            return [...optionsArray];
        }

        const selectedSet = new Set(selectedIds);
        const sortedOptions = [...optionsArray].sort((a, b) => {
            const aId = a[localIdField].toString();
            const bId = b[localIdField].toString();
            const aSelected = selectedSet.has(aId);
            const bSelected = selectedSet.has(bId);

            // Selected always come first
            if (aSelected && !bSelected) return -1;
            if (!aSelected && bSelected) return 1;

            // If both are selected or both unselected, sort alphabetical by label
            return a[localLabelField].localeCompare(b[localLabelField]);
        });
        return sortedOptions;
    };

    const itemName = props.itemName ?? "Item";

    const shouldShowControls = compactLayoutEnabled
        ? options.length > controlsThreshold
        : true;

    const selectedCountLabel = `${currentSelectedIds.length} ${
        currentSelectedIds.length === 1 ? itemName : itemName + "s"
    } Selected`;

    const filteredOptions: T[] = useMemo(() => {
        return options.filter((o) =>
            o[localLabelField].toLowerCase().includes(searchTerm.toLowerCase()),
        );
    }, [options, searchTerm, localLabelField]);

    const filteredIds: string[] = useMemo(() => {
        return filteredOptions.map((o) => o[localIdField].toString());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localIdField, filteredOptions]);

    const handleOpen: React.MouseEventHandler<HTMLDivElement> | undefined =
        () => {
            if (props.disabled) return;
            setOptions(sortOptions(options, currentSelectedIds));
            setIsOpen((prev) => !prev);
        };

    const applySelectedIds = (newSelectedIds: string[]) => {
        if (areSelectedIdsEqual(currentSelectedIds, newSelectedIds)) return;

        setSelectedIds(newSelectedIds);
        props.onApply?.(newSelectedIds);
    };

    const handleChange = (
        val: React.ChangeEvent<HTMLInputElement>,
        option: T,
    ) => {
        let newSelectedIds: string[] = [];

        const optionId = option[localIdField].toString();

        if (val.target.checked) {
            newSelectedIds = [...currentSelectedIds, optionId];
        } else {
            newSelectedIds = currentSelectedIds.filter((i) => i !== optionId);
        }

        applySelectedIds(newSelectedIds);
    };

    const handleSelectAll = () => {
        const max = props.maxSelectionCount ?? Infinity;
        const setCopy = new Set(currentSelectedIds);

        const remaining = max - currentSelectedIds.length;

        // we disable when at maxSelection so this is just a safety check
        if (remaining <= 0) return;

        // filter out already-selected IDs
        const unselected = filteredIds.filter((id) => !setCopy.has(id));

        const newlyAddedIds = unselected.slice(0, remaining);

        applySelectedIds([...currentSelectedIds, ...newlyAddedIds]);
    };

    const handleClickAway = () => {
        setIsOpen((prev) => !prev);
    };

    // clear only selected ids from currently filtered items
    const handleClear = () => {
        const filterIdSet = new Set(filteredIds);
        const newSelectedIds = currentSelectedIds.filter(
            (i) => !filterIdSet.has(i),
        );

        applySelectedIds(newSelectedIds);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (props.disabled) return;
        if (event.code === "Escape") {
            setIsOpen(false);
        } else if (event.code === "Space") {
            // only want to open on space press, not close
            // other selects close when pressing space because it also selects and they close on select
            // this select does not close on select by design
            setIsOpen(true);
        }
    };

    useEffect(() => {
        if (!props.sortOnOpenOnly) {
            setOptions(sortOptions(options, selectedIds));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedIds]);

    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            searchInputRef.current?.focus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, searchInputRef.current]);

    // keep selectedIds updated when external controlled value changes (e.g. clearing filters)
    useEffect(() => {
        if (!areSelectedIdsEqual(selectedIds, props.defaultSelectedIds)) {
            setSelectedIds(props.defaultSelectedIds ?? []);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.defaultSelectedIds]);

    // update options when loaded
    useEffect(() => {
        setOptions(props.options);
    }, [props.options]);

    const maxSelected = props.maxSelectionCount
        ? currentSelectedIds.length >= props.maxSelectionCount
        : false;

    const renderCheckboxItem = (option: T, style: CSSProperties) => {
        const optionLabel = option[localLabelField];
        const optionId = option[localIdField].toString();

        const isSelected = currentSelectedIds.includes(optionId);

        return (
            <Grid
                container
                item
                xs={12}
                sx={{
                    backgroundColor: isSelected
                        ? theme.palette.lightBlue
                        : theme.palette.white.main,
                    paddingLeft: theme.spacing(1.5),
                    paddingRight: theme.spacing(1),
                    height: "fit-content",
                    "&:hover": {
                        backgroundColor: theme.palette.lightBlue,
                    },
                }}
                style={style}
                key={optionId}
                margin={theme.spacing(0.1)}
            >
                <FormControlLabel
                    control={
                        <Checkbox
                            onChange={(val) => handleChange(val, option)}
                            name={optionLabel}
                            checked={isSelected}
                            disabled={!isSelected && maxSelected}
                            sx={{
                                padding: theme.spacing(0.5),
                            }}
                        />
                    }
                    label={
                        <Box
                            width={
                                wrapOptionLabels
                                    ? wrappedLabelWidth
                                    : menuWidth - 40
                            }
                            height={listItemHeight}
                            display={"flex"}
                            alignItems={"center"}
                            sx={
                                isSelected
                                    ? { color: theme.palette.primary.main }
                                    : {
                                          "&:hover": {
                                              color:
                                                  !isSelected && maxSelected
                                                      ? null
                                                      : theme.palette.primary
                                                            .main,
                                          },
                                      }
                            }
                        >
                            {props.renderOptionLabel ? (
                                props.renderOptionLabel(option)
                            ) : (
                                <Typography
                                    noWrap={!wrapOptionLabels}
                                    variant="inherit"
                                    title={optionLabel}
                                    sx={
                                        wrapOptionLabels
                                            ? wrappedOptionLabelSx
                                            : { minWidth: 0 }
                                    }
                                >
                                    {optionLabel}
                                </Typography>
                            )}
                        </Box>
                    }
                    style={{
                        overflowWrap: "anywhere",
                    }}
                    labelPlacement={"end"}
                />
            </Grid>
        );
    };

    const searchInputIcon = props.isLoading ? (
        <Grid
            style={{
                paddingRight: theme.spacing(1),
            }}
        >
            <CircularProgress size={18} />
        </Grid>
    ) : (
        <IconButton size="small" onClick={() => setSearchTerm("")}>
            <ClearIcon fontSize="small" />
        </IconButton>
    );

    const searchInput = (
        <Grid
            item
            xs={12}
            style={{
                padding: theme.spacing(2),
                paddingBottom: 0,
            }}
        >
            <AcxMainTextField
                id={props.id + "-search-compact-select-multi"}
                value={searchTerm}
                placeholderText={"Search..."}
                onChange={(e) => setSearchTerm(e.target.value)}
                endAdornment={searchInputIcon}
                fullWidth
                isDisabled={props.isLoading}
                focus
                ref={searchInputRef}
            />
        </Grid>
    );

    const headerButtons = shouldShowControls ? (
        <Grid container justifyContent="space-between" alignItems="center">
            <Grid item margin={theme.spacing(1)}>
                <AcxButton
                    fullWidth
                    id={props.id + "-apply-button"}
                    color="primary"
                    variant="text"
                    onClick={handleSelectAll}
                    disabled={maxSelected}
                >
                    {props.showItemNameInSelectAll
                        ? `Select All ${itemName}s`
                        : "Select All"}
                </AcxButton>
            </Grid>
            <Grid item margin={theme.spacing(1)}>
                <AcxButton
                    fullWidth
                    id={props.id + "-clear-button"}
                    onClick={handleClear}
                    color="white"
                    variant="text"
                >
                    Clear
                </AcxButton>
            </Grid>
        </Grid>
    ) : null;

    const calculatedListHeight = compactLayoutEnabled
        ? Math.min(
              Math.max(filteredOptions.length, 1) * listItemHeight,
              maxListHeight,
          )
        : maxListHeight;

    const virtualizedList = (
        <List
            itemCount={filteredOptions.length}
            itemSize={listItemHeight}
            itemData={filteredOptions}
            height={calculatedListHeight}
            width={menuWidth}
            style={{
                overflowX: "hidden",
                borderBottomRightRadius: theme.shape.borderRadius,
                borderBottomLeftRadius: theme.shape.borderRadius,
            }}
        >
            {({ index, style }) => {
                return renderCheckboxItem(filteredOptions[index], style);
            }}
        </List>
    );

    const popperComponent = (
        <ClickAwayListener onClickAway={handleClickAway}>
            <Popper
                open={isOpen}
                anchorEl={ref.current}
                placement={isMenuWiderThanSelect ? "bottom-start" : "bottom"}
                style={{
                    zIndex: theme.zIndex.modal,
                }}
                onKeyDown={handleKeyDown}
            >
                <Paper
                    id={props.id + "-popper-container"}
                    style={{
                        width: menuWidth,
                        boxShadow: theme.shadows[2],
                        marginTop: theme.spacing(1),
                        overflow: "hidden",
                    }}
                >
                    {shouldShowControls && searchInput}
                    {props.warningMessage &&
                        props.maxSelectionCount &&
                        selectedIds.length === props.maxSelectionCount && (
                            <Alert sx={{ mx: 2, mt: 1 }} severity="warning">
                                {props.warningMessage}
                            </Alert>
                        )}
                    {headerButtons}

                    <Divider />

                    {virtualizedList}
                </Paper>
            </Popper>
        </ClickAwayListener>
    );

    return (
        <>
            <Grid
                container
                justifyContent="space-between"
                onClick={handleOpen}
                sx={{
                    backgroundColor: hasReadOnlyDisabledAppearance
                        ? "transparent"
                        : theme.palette.white.main,
                    fontSize: "13px",
                    border: hasReadOnlyDisabledAppearance
                        ? "1px solid"
                        : "0.5px solid",
                    borderColor: isOpen
                        ? theme.palette.primary.main
                        : theme.palette.lightgrayBorder.main,
                    borderRadius: 1,
                    minHeight: "32px",
                    height: "32px",
                    cursor: props.disabled ? "default" : "pointer",
                    opacity:
                        props.disabled && !hasReadOnlyDisabledAppearance
                            ? 0.5
                            : 1,
                    padding: "6px",
                    paddingLeft: "8px",
                }}
                ref={ref}
                id={props.id}
                wrap="nowrap"
                onKeyDown={handleKeyDown}
                tabIndex={props.disabled ? -1 : 0}
                aria-disabled={props.disabled}
            >
                <Typography noWrap>{selectedCountLabel}</Typography>

                {!hasReadOnlyDisabledAppearance && (
                    <Grid item>
                        <DownChevronSvg />
                    </Grid>
                )}
            </Grid>
            {isOpen && popperComponent}
        </>
    );
});

export default AcxVirtualizedSelectMulti;
